/**
 * One-shot cross-route focus handoffs. Module-level flags (not router state)
 * because the SPA router carries no navigation payload; a full page load
 * simply drops a pending request, which is the right behaviour — the browser
 * already resets focus.
 *
 * Two tiers:
 *
 * - The *main-heading* handoff is page-specific and precise: the create
 *   wizard requests focus before navigating to the freshly created
 *   specialist's detail page; the detail page consumes the request when its
 *   main heading mounts and moves focus there, so keyboard-only operators are
 *   not stranded at a stale focus position after the SPA navigation.
 *
 * - The *route focus reset* is the generic handoff requested by every
 *   `navigate(...)` push: the shell leaves focus in place when it is already
 *   inside main; otherwise it waits for and focuses the new page's h1. It
 *   stands down when a main-heading handoff is pending so the precise consumer
 *   keeps ownership, and because only `navigate` requests it, the very first
 *   render never has focus stolen.
 */

let pendingMainHeading = false;
let pendingRouteReset = false;

export function requestMainHeadingFocus(): void {
  pendingMainHeading = true;
}

export function consumeMainHeadingFocus(): boolean {
  const wasPending = pendingMainHeading;
  pendingMainHeading = false;
  return wasPending;
}

/** True while a page-specific heading handoff is waiting for its consumer. */
export function hasPendingMainHeadingFocus(): boolean {
  return pendingMainHeading;
}

export function requestRouteFocusReset(): void {
  pendingRouteReset = true;
}

export function consumeRouteFocusReset(): boolean {
  const wasPending = pendingRouteReset;
  pendingRouteReset = false;
  return wasPending;
}
